import { useApolloClient } from "@apollo/client";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import {
  RoutineSessionsQuery,
  RoutineSessionsQueryVariables,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import GraphqlError from "@components/error/graphql-error";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { assertEdgesNonNull } from "@helpers/helpers";

import routineSessionsQuery from "../../graphql/routine-sessions-query";

const PAGE_SIZE = 50;

type SessionNode = { id: number; created: string };

type Props = {
  routineId: string;
  title: string;
  isOpen: boolean;
  onClose: () => void;
};

const RoutineSessionsModal: React.FC<Props> = ({
  routineId,
  title,
  isOpen,
  onClose,
}) => {
  const client = useApolloClient();
  const [sessions, setSessions] = useState<SessionNode[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // `no-cache` keeps this per-routine list out of the shared chatSessions cache
  // entry that the global chat history uses, so opening the modal can't clobber it.
  const fetchPage = useCallback(
    async (after: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await client.query<
          RoutineSessionsQuery,
          RoutineSessionsQueryVariables
        >({
          query: routineSessionsQuery,
          variables: { routineId, first: PAGE_SIZE, after },
          fetchPolicy: "no-cache",
        });
        const connection = data.chatSessions;
        const nodes = assertEdgesNonNull(connection) as SessionNode[];
        setSessions((prev) => (after ? [...prev, ...nodes] : nodes));
        setHasNextPage(connection?.pageInfo.hasNextPage ?? false);
        setEndCursor(connection?.pageInfo.endCursor ?? null);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [client, routineId],
  );

  // Load the first page fresh each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setSessions([]);
    setHasNextPage(false);
    setEndCursor(null);
    fetchPage(null);
  }, [isOpen, fetchPage]);

  if (!isOpen) return null;

  return (
    <Modal
      open
      alignedTop
      onClose={onClose}
      aria-label="Routine sessions dialog"
    >
      <div className="p-6 bg-white rounded-md">
        <div className="mb-4">
          <ModalTitle onClose={onClose}>{title} — sessions</ModalTitle>
        </div>

        {error ? (
          <GraphqlError
            error={error}
            whatDidNotWork="These sessions could not be loaded."
          />
        ) : loading && sessions.length === 0 ? (
          <Loading mini size={6} />
        ) : sessions.length === 0 ? (
          <div className="text-sm text-gray-500">No runs yet.</div>
        ) : (
          <div
            className="flex flex-col gap-1"
            aria-label="Routine sessions list"
          >
            {sessions.map((session) => (
              <AppLink
                key={session.id}
                to={`/ask-ai/${session.id}`}
                className="text-sm text-blue-600 hover:text-blue-800 py-1"
              >
                {moment(session.created).format("MMM D, YYYY h:mm A")}
              </AppLink>
            ))}
          </div>
        )}

        {hasNextPage && (
          <div className="mt-4">
            <Button
              theme={buttonTheme.lightGray}
              size={ButtonSize.medium}
              text="Load more"
              aria-label="Load more sessions"
              disabled={loading}
              onClick={() => fetchPage(endCursor)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RoutineSessionsModal;
